<script lang="ts">
	import PageGrid16 from '../components/pageGrid16.svelte';
	import Carousel from '$lib/carousel.svelte';
	import type { SbImage, SbVideo } from '../types';
	import { fly } from 'svelte/transition';
	import Button from '$lib/buttons/v3/button.svelte';
	import ArrowRight from '$lib/icons/arrowRight.svelte';
	import ArrowLeft from '$lib/icons/arrowLeft.svelte';
	import type { EmblaCarouselType } from 'embla-carousel';
	import type { ISbStoryData } from '@storyblok/js';
	import { editable } from '../editable';
	import Blok from '../blok.svelte';
	import { onDestroy } from 'svelte';

	interface Props {
		content: ISbStoryData<{
			headline: string;
			subline: string;
			items: Array<ISbStoryData<{ asset: SbImage | SbVideo; text?: string }>['content']>;
		}>['content'];
	}

	let { content }: Props = $props();

	let embla = $state<EmblaCarouselType>();
	let currentIndex = $state(0);
	let lastSlideFullyVisible = $state(false);

	function isLastSlideFullyVisible() {
		const lastSlide: HTMLElement | null = document.querySelector('.slide:last-child');
		if (!lastSlide) return false;

		const { right } = lastSlide.getBoundingClientRect();

		return right <= window.innerWidth;
	}

	function checkLastSlideVisibility() {
		lastSlideFullyVisible = isLastSlideFullyVisible();
	}

	$effect(() => {
		if (embla) {
			embla.on('scroll', checkLastSlideVisibility);
		}
	});

	onDestroy(() => {
		embla?.off('scroll', checkLastSlideVisibility);
	});
</script>

<section use:editable={content} class="relative py-14 text-brown-1 max-lg:px-5 lg:py-23">
	<PageGrid16 gridClass="gap-y-3 lg:gap-y-12">
		{#if content.headline?.length > 0}
			<h1
				class="col-span-full text-balance text-[36px]/[112%] font-semibold tracking-[-.36px] lg:col-start-2 lg:col-end-7 lg:text-[48px]/[120%] lg:tracking-[-.48px]"
			>
				{content.headline}
			</h1>
		{/if}
		{#if content.subline?.length > 0}
			<h2
				class="col-span-full text-balance text-[16px]/[150%] tracking-[-.16px] lg:col-start-9 lg:col-end-[16] lg:text-[20px]/[140%] lg:tracking-[-.2px]"
			>
				{content.subline}
			</h2>
		{/if}

		<Carousel
			overflow
			bind:embla
			bind:currentIndex
			containerClass="col-span-full lg:col-start-2 lg:col-end-[16] max-lg:mt-3"
			class="flex h-full gap-x-5"
		>
			{#each content.items as item}
				<Blok content={item} />
			{/each}
		</Carousel>
	</PageGrid16>
	<div class="pointer-events-none absolute inset-0 mt-23 max-lg:hidden">
		{#if currentIndex > 0}
			<div
				transition:fly={{ x: '-100%', opacity: 1, delay: 100 }}
				class="absolute inset-y-0 left-0 flex items-center pl-5"
			>
				<Button class="btn-large btn-white pointer-events-auto" onclick={() => embla?.scrollPrev()}
					><ArrowLeft /></Button
				>
			</div>
		{/if}
		{#if !lastSlideFullyVisible}
			<div
				transition:fly={{ x: '100%', opacity: 1, delay: 100 }}
				class="absolute inset-y-0 right-0 flex items-center pr-5"
			>
				<Button class="btn-large btn-white pointer-events-auto" onclick={() => embla?.scrollNext()}
					><ArrowRight /></Button
				>
			</div>
		{/if}
	</div>
</section>
